import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Tooltip,
  CircularProgress
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import FilterListIcon from '@mui/icons-material/FilterList';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useLanguage } from '../../../utils/i18n';
import { motion, AnimatePresence } from 'framer-motion';

import VehLocDialog from './VehLocDialog';

function VehicleListDialog({ open, onClose, selectedStat }) {
  return <VehLocDialog open={open} onClose={onClose} selectedStat={selectedStat} />;
}
// Removing the rest of the old VehicleListDialog

export default function VehicleLocation({ data, polygonTypes, loading }) {
  const { t } = useLanguage();
  const [anchorEl, setAnchorEl] = useState(null);
  const [hiddenCards, setHiddenCards] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStat, setSelectedStat] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [direction, setDirection] = useState(1);
  const itemsPerPage = 6;

  const getStatVal = (obj) => obj || { total: 0, atLocation: 0, delay: 0, vehicles: [] };

  const stats = [
    { title: 'Pit', dataObj: getStatVal(data?.pit), color: '#1976d2' },
    { title: 'Jetty', dataObj: getStatVal(data?.jetty), color: '#8e24aa' }
  ];

  if (polygonTypes && Array.isArray(polygonTypes)) {
    polygonTypes.forEach((pt) => {
      // Avoid duplicating Pit and Jetty if they somehow exist in polygonType as well
      const titleLower = String(pt.name).toLowerCase();
      if (titleLower !== 'pit' && titleLower !== 'jetty') {
        stats.push({
          title: pt.name.charAt(0).toUpperCase() + pt.name.slice(1),
          dataObj: getStatVal(data?.dynamicGeoCounts?.[pt.name]),
          color: pt.fill_color || '#ffb74d'
        });
      }
    });
  }

  const handleToggleHide = (title) => {
    setHiddenCards((prev) => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  const visibleStats = stats.filter((stat) => !hiddenCards[stat.title]);
  const totalPages = Math.ceil(visibleStats.length / itemsPerPage);

  useEffect(() => {
    // Reset page if visible items become fewer than the current page
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(0);
    }
  }, [visibleStats.length, totalPages, currentPage]);

  useEffect(() => {
    let interval;
    if (!dialogOpen && totalPages > 1) {
      interval = setInterval(() => {
        setDirection(1);
        setCurrentPage((prev) => (prev + 1) % totalPages);
      }, 10000);
    }
    return () => clearInterval(interval);
  }, [dialogOpen, totalPages]);

  const handleNext = () => {
    setDirection(1);
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const handlePrev = () => {
    setDirection(-1);
    setCurrentPage((prev) => (prev === 0 ? totalPages - 1 : prev - 1));
  };

  const currentVisibleStats = visibleStats.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

  return (
    <>
      <Paper
        elevation={0}
        variant="outlined"
        sx={{ p: 2.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', height: '100%' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                bgcolor: '#4c5c96',
                color: 'white',
                borderRadius: 1.5,
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <LocationOnIcon fontSize="small" />
            </Box>
            <Typography variant="h4" fontWeight="bold">
              {t('revenue.vehicleLocation', 'Vehicle Location')}
            </Typography>
          </Box>
          <Box>
            <Tooltip title={t('general.filter', 'Filter')}>
              <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ bgcolor: 'white', border: '1px solid #e2e8f0' }}>
                <FilterListIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {stats.map((stat) => (
                <MenuItem key={stat.title} onClick={() => handleToggleHide(stat.title)} sx={{ py: 0, px: 1 }}>
                  <Checkbox checked={!hiddenCards[stat.title]} size="small" />
                  <ListItemText primary={stat.title} />
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Box>

        <Box sx={{ position: 'relative', overflow: 'hidden', minHeight: 120 }}>
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentPage}
              custom={direction}
              initial={{ opacity: 0, x: direction * 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: direction * -50 }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
            >
              <Grid container spacing={2}>
                {currentVisibleStats.map((stat, index) => (
                  <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                    <Paper
                      variant="outlined"
                      onClick={() => {
                        setSelectedStat(stat);
                        setDialogOpen(true);
                      }}
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderRadius: 2,
                        borderColor: 'divider',
                        borderWidth: 2,
                        borderStyle: 'solid',
                        boxShadow: 1,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: stat.color,
                          boxShadow: 3
                        }
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600} color="text.primary" sx={{ display: 'block', mb: 0.5 }}>
                          {t('revenue.at', 'At')} {stat.title}
                        </Typography>
                        <Typography
                          variant="h4"
                          fontWeight="bold"
                          color="text.primary"
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          {loading ? <CircularProgress size={24} color="inherit" /> : stat.dataObj.total}
                          <Typography variant="caption" color="text.secondary" fontWeight="normal" sx={{ alignSelf: 'baseline' }}>
                            Total
                          </Typography>
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 8, mt: 0.5 }}>
                          <Typography
                            variant="caption"
                            color="primary.main"
                            fontWeight={500}
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                          >
                            GPS Update: {loading ? <CircularProgress size={12} color="inherit" /> : stat.dataObj.atLocation}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="warning.main"
                            fontWeight={500}
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                          >
                            Delay: {loading ? <CircularProgress size={12} color="inherit" /> : stat.dataObj.delay}
                          </Typography>
                        </Box>
                      </Box>
                      <Avatar sx={{ bgcolor: stat.color, color: 'white', width: 40, height: 40 }}>
                        <DirectionsCarIcon />
                      </Avatar>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </AnimatePresence>
        </Box>

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, gap: 1 }}>
            <IconButton size="small" onClick={handlePrev} sx={{ border: '1px solid #e2e8f0', bgcolor: 'white' }}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 40, textAlign: 'center' }}>
              {currentPage + 1} / {totalPages}
            </Typography>
            <IconButton size="small" onClick={handleNext} sx={{ border: '1px solid #e2e8f0', bgcolor: 'white' }}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
        )}
      </Paper>

      <VehicleListDialog open={dialogOpen} onClose={() => setDialogOpen(false)} selectedStat={selectedStat} />
    </>
  );
}
